import { SwapOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, message, Spin } from "antd";
import CustomPopconfirm from "../Popover/CustomPopconfirm";
import { FormController, TFormStructure } from "formifex";
import { useCallback, useEffect, useState } from "react";
import { getTenantSpecificUsers } from "../../services/api-server/graphql";

import { useTenantStore } from "../../stores/tenantStore";
import { useUserStore } from "../../stores/userStore";
import { updateIncidentReportItem } from "../../services/api-server/incident_report";
import { updateAuditsItem } from "../../services/api-server/audits";
import { updateMoCItem } from "../../services/api-server/moc";
import { updateOFIItem } from "../../services/api-server/ofi";
import { updateNonConformanceItem } from "../../services/api-server/non_conformance";
import { updateLessonLearnedItem } from "../../services/api-server/lesson_learned";
import { refreshControlCenter } from "../../services/api-server/control_center";
import { InputSkeletonLoading } from "../Skeleton/CustomSkeleton";
import Emitter from "../../utils/emitter";

/* ─── Constants ─── */
const STATUS_TEXT_MAP: Record<string, string> = {
	draft: "Draft",
	in_review: "Initial review",
	in_progress: "In progress",
	final_review: "Final review",
	ready_to_close: "Approved - Open",
	changes_required: "Changes requested",
	revision_required: "Revision required",
	closed_approved: "Approved and closed",
	closed_rejected: "Rejected and closed",
};

const ALL_STATUSES = Object.keys(STATUS_TEXT_MAP);

/* ─── Types ─── */
interface ReportStatusUpdateModalProps {
	reportId: string;
	reportType: string;
	operator?: string;
	taskId?: string;
	currentStatus?: string;
	currentAssignedTo?: string;
	disabled?: boolean;
	onSuccess?: () => void;
}

interface UserOption {
	email: string;
	name?: string;
}

/* ─── Pure helpers ─── */
const translateStatus = (combinedStatus: string) => {
	if (combinedStatus === "draft") {
		return { status: "draft", open_status: undefined, is_rejected: undefined };
	}
	if (combinedStatus === "closed_approved") {
		return { status: "closed", open_status: undefined, is_rejected: false };
	}
	if (combinedStatus === "closed_rejected") {
		return { status: "closed", open_status: undefined, is_rejected: true };
	}
	return {
		status: "open",
		open_status: combinedStatus,
		is_rejected: undefined,
	};
};

// Map report types to their update functions
const REPORT_UPDATE_API: Record<string, Function> = {
	incident_report: updateIncidentReportItem,
	audit: updateAuditsItem,
	moc: updateMoCItem,
	ofi: updateOFIItem,
	non_conformance: updateNonConformanceItem,
	lesson_learned: updateLessonLearnedItem,
};

const buildFormSchema = (
	users: UserOption[],
	reportType?: string
): TFormStructure => {
	const auditExcludedStatuses: string[] = [];
	const rejectedAndClosedExcludedForAllExceptOfi =
		reportType === "ofi" ? [] : ["closed_rejected"];
	const readyToCloseOnlyForNcr =
		reportType === "non_conformance" ? [] : ["ready_to_close"];
	const lessonLearnedExcludedStatuses =
		reportType === "lesson_learned"
			? ALL_STATUSES.filter(
					(status) =>
						![
							"draft",
							"in_review",
							"final_review",
							"changes_required",
							"closed_approved",
						].includes(status)
				)
			: [];
	const excludedStatuses = [
		...auditExcludedStatuses,
		...rejectedAndClosedExcludedForAllExceptOfi,
		...readyToCloseOnlyForNcr,
		...lessonLearnedExcludedStatuses,
	];
	const availableStatuses = ALL_STATUSES.filter(
		(status) => !excludedStatuses.includes(status)
	);

	return {
		id: "update-report-status",
		requiredMark: "optional",
		layout: "vertical",
		items: [
			{
				id: "new_status",
				label: "New status",
				type: "input",
				dataType: "string",
				inputType: "select",
				options: availableStatuses.map((status) => ({
					label: STATUS_TEXT_MAP[status] || status,
					value: status,
				})),
				required: true,
			},
			{
				id: "reason",
				label: "Reason",
				type: "input",
				dataType: "string",
				inputType: "textarea",
				required: false,
				properties: {
					autoSize: { minRows: 2, maxRows: 9999 },
					showCount: true,
					maxLength: 100,
				},
				style: { backgroundColor: "transparent" },
			},
			{
				id: "reassign_to",
				label: "Reassign to",
				type: "input",
				dataType: "string",
				inputType: "select",
				options: users.map((user) => ({
					label: user.name || user.email,
					value: user.email,
				})),
				placeholder: "Select user to reassign",
				required: false,
				properties: { showSearch: true, allowClear: true },
				displayWhen: [
					{ field: "new_status", operator: "!=", value: "in_progress" },
					{ field: "new_status", operator: "!=", value: "revision_required" },
					{ field: "new_status", operator: "!=", value: "closed_approved" },
					{ field: "new_status", operator: "!=", value: "closed_rejected" },
				],
			},
		],
	};
};
/* ─── Component ─── */
export const ReportStatusUpdateModal = ({
	reportId,
	reportType,
	operator,
	taskId,
	currentStatus,
	currentAssignedTo,
	disabled = false,
	onSuccess,
}: ReportStatusUpdateModalProps) => {
	const [open, setOpen] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [usersList, setUsersList] = useState<UserOption[]>([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);

	const form = FormController.useFormController();
	const queryClient = useQueryClient();
	const tenantList = useTenantStore((state) => state.tenantList);
	const currentUser = useUserStore((state) => state.user);
	const isDarkTheme = localStorage.getItem("theme") === "dark";

	// Resolve assignee name from users list or fallback to email
	const resolveAssigneeName = useCallback(
		(email?: string) => {
			if (!email) return "—";
			const user = usersList.find(
				(u) => u.email?.toLowerCase() === email.toLowerCase()
			);
			return user?.name || email;
		},
		[usersList]
	);

	// Fetch users when popconfirm opens
	useEffect(() => {
		if (!open) {
			setUsersList([]);
			setValidationError(null);
			return;
		}

		const abortController = new AbortController();
		let isCancelled = false;

		const fetchUsers = async () => {
			setIsLoadingUsers(true);
			setValidationError(null);

			try {
				// Find tenant UUID from tenant list using operator (role_key)
				const tenant = tenantList.find((t) => t.role_key === operator);
				const tenantUuid = tenant?.uuid;
				const fetchedUsers = tenantUuid
					? await getTenantSpecificUsers(tenantUuid)
					: [];

				if (isCancelled || abortController.signal.aborted) return;

				const users =
					Array.isArray(fetchedUsers) && fetchedUsers.length > 0
						? fetchedUsers.map((user: any) => ({
								email: user.email || user.mail,
								name: user.name || user.displayName || user.email || user.mail,
							}))
						: [];

				setUsersList(users);

				// Build and set schema
				const schema = buildFormSchema(users, reportType);
				form.setSchema(schema);
			} catch (error: any) {
				if (isCancelled || abortController.signal.aborted) return;
				console.error("Failed to fetch users", error);
				setUsersList([]);
				setValidationError("Failed to load data. Please try again.");
			} finally {
				if (!isCancelled && !abortController.signal.aborted) {
					setIsLoadingUsers(false);
				}
			}
		};

		fetchUsers();

		return () => {
			isCancelled = true;
			abortController.abort();
		};
	}, [open, operator]);

	const updateStatusMutation = useMutation({
		mutationFn: async ({
			status,
			reason,
			reassignTo,
		}: {
			status: string;
			reason?: string;
			reassignTo?: string;
		}) => {
			// Translate combined status to status + open_status + is_rejected
			const {
				status: reportStatus,
				open_status,
				is_rejected,
			} = translateStatus(status);

			// Build update data
			const updateData: Record<string, any> = {
				status: reportStatus,
			};
			if (open_status !== undefined) updateData.open_status = open_status;
			if (is_rejected !== undefined && is_rejected !== null)
				updateData.is_rejected = is_rejected;
			if (reason) updateData.reason = reason;

			// Closing a report from the Control Center should always force-close,
			// even if follow-up actions/findings are still pending, and records the
			// close date like the normal close path does.
			if (status === "closed_approved" || status === "closed_rejected") {
				updateData.is_force_close = true;
				updateData.closed_on = new Date();
			}

			// Explicitly downgrading a report from final review to in progress from the
			// Control Center should force all non-already-completed actions/findings back
			// to draft and create new tasks for every responsible person.
			if (
				(reportType === "audit" || reportType === "non_conformance") &&
				currentStatus === "final_review" &&
				status === "in_progress"
			) {
				updateData.is_force_in_progress = true;
			}

			// Map reassign_to to the correct field based on status
			// Draft / Changes Requested -> author
			// In Review / Final Review / Approved - Open -> reviewer
			if (reassignTo) {
				if (reportType === "lesson_learned") {
					if (status === "draft" || status === "changes_required") {
						updateData.primary_author = reassignTo.toLowerCase();
					} else if (status === "in_review") {
						updateData.reviewer = reassignTo;
					} else if (status === "final_review") {
						updateData.commenter = reassignTo;
					}
				} else {
					if (status === "draft" || status === "changes_required") {
						updateData.author = reassignTo;
					} else if (
						status === "in_review" ||
						status === "final_review" ||
						status === "ready_to_close"
					) {
						updateData.reviewer = reassignTo;
					}
				}
			}

			// Use report type's /update endpoint to trigger task creation
			const updateFn = REPORT_UPDATE_API[reportType];
			if (!updateFn) {
				throw new Error(`No update function for report type: ${reportType}`);
			}

			const reportResult = await updateFn(
				reportId,
				updateData,
				{ email: currentUser?.email, name: currentUser?.name },
				operator || ""
			);

			return reportResult;
		},
		onMutate: () => {
			setIsUpdating(true);
			setValidationError(null);
		},
		onSuccess: async () => {
			message.success("Status updated successfully");
			setOpen(false);
			form.resetForm();

			// Refresh control center cache to show updated data immediately
			try {
				await refreshControlCenter();
			} catch (error) {
				console.warn("Failed to refresh control center cache:", error);
			}

			// Refetch the real Control Center query keys (the previous keys were unused)
			try {
				await queryClient.refetchQueries({
					queryKey: ["control_center_counts"],
				});
				await queryClient.refetchQueries({
					queryKey: ["control_center_filter_options"],
				});
			} catch (error) {
				console.warn("Failed to refetch control center queries:", error);
			}

			queryClient.invalidateQueries({
				queryKey: ["my_tasks"],
			});

			// Notify ControlCenter to reload its local section state
			Emitter.emit("controlCenterUpdated", null);
			onSuccess?.();
		},
		onError: (error: any) => {
			message.error(error?.message || "Failed to update status");
			setValidationError(error?.message || "Failed to update status");
		},
		onSettled: () => {
			setIsUpdating(false);
		},
	});

	const handleConfirm = async () => {
		try {
			setValidationError(null);
			await form.formRef.validateFields();
			const formData = form.formData;
			await updateStatusMutation.mutateAsync({
				status: formData.new_status,
				reason: formData.reason,
				reassignTo: formData.reassign_to || undefined,
			});
		} catch (error: any) {
			// Form validation errors are handled by Formifex
			// Only log unexpected errors
			if (error?.errorFields) {
				console.log("Form validation failed", error.errorFields);
			} else {
				console.error("Failed to update status", error);
				setValidationError(error?.message || "Failed to update status");
			}
		}
	};

	const handleCancel = () => {
		setOpen(false);
		form.resetForm();
		setValidationError(null);
	};

	return (
		<CustomPopconfirm
			classNames={{
				body: isDarkTheme
					? "bg-[#505071]! rounded-sm!"
					: "bg-white! rounded-sm! border border-gray-200!",
			}}
			title={
				<div className={`px-2 py-2 ${isDarkTheme ? "" : "text-gray-800"}`}>
					Update report status
				</div>
			}
			open={open}
			handleConfirm={handleConfirm}
			onCancel={handleCancel}
			okText="Save"
			cancelText="Cancel"
			okButtonProps={{
				loading: isUpdating,
				disabled: isUpdating || !form.formData?.new_status,
			}}
			icon={null}
			placement="bottomRight"
			description={
				<div className="flex flex-col gap-3 w-[400px] mt-3 px-2">
					{/* Current status and Assigned to */}
					<div
						className={`flex px-3 py-2 items-center gap-[18px] self-stretch ${
							isDarkTheme ? "bg-[rgba(255,255,255,0.4)]" : "bg-gray-100"
						}`}
					>
						<div className="flex-1 flex-col justify-center items-center">
							<div
								className={`text-sm mb-1 ${
									isDarkTheme
										? "text-[rgba(255,255,255,0.85)]"
										: "text-gray-600"
								}`}
							>
								Current status
							</div>
							<div
								className={`text-sm truncate ${
									isDarkTheme ? "" : "text-gray-800"
								}`}
							>
								{STATUS_TEXT_MAP[currentStatus || ""] || currentStatus || "—"}
							</div>
						</div>
						<div className="flex-1 flex-col justify-center items-center">
							<div
								className={`text-sm mb-1 ${
									isDarkTheme
										? "text-[rgba(255,255,255,0.85)]"
										: "text-gray-600"
								}`}
							>
								Assigned to
							</div>
							<div
								className={`text-sm truncate ${
									isDarkTheme ? "" : "text-gray-800"
								}`}
							>
								<InputSkeletonLoading
									loading={isLoadingUsers}
									children={resolveAssigneeName(currentAssignedTo)}
								/>
							</div>
						</div>
					</div>

					{/* Error message */}
					{validationError && (
						<div className="text-red-400 text-sm px-2">{validationError}</div>
					)}

					{/* Formifex form */}
					<div className="mt-2">
						{isLoadingUsers ? (
							<div className="flex justify-center py-4">
								<Spin size="small" />
							</div>
						) : (
							<FormController
								key={`report-status-update-${reportId}-${taskId || "no-task"}`}
								formController={form}
								theme={isDarkTheme ? "dark" : "light"}
								readOnly={isUpdating}
							/>
						)}
					</div>
				</div>
			}
		>
			<Button
				type="link"
				icon={<SwapOutlined />}
				onClick={() => setOpen(true)}
				disabled={disabled}
			>
				Update Status
			</Button>
		</CustomPopconfirm>
	);
};

export default ReportStatusUpdateModal;
